import * as React from 'react';

import {
  AsyncBoundary,
  Badge,
  Button,
  Card,
  cn,
  Col,
  LocalizedDate,
  Row,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideArrowLeft,
  LucideChevronDown,
  LucideChevronRight,
  LucideCircleCheck,
  LucideCircleX,
  LucideMinusCircle,
  LucideZap,
  LucideMail,
  LucideGlobe,
  LucideGitBranch,
  LucideTimer,
  LucideShieldCheck,
  LucideCalendar,
} from 'lucide-react';
import { useLocation } from 'wouter';

import {
  $executionRunAutomationId,
  $remoteExecutionRunDetail,
} from '../../_state/executionRunDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { automationsRoutes } from '../../routes';
import type { NodeExecutionRecord, NodeExecutionStatus } from '../../types';

import { ExecutionStatusBadge } from './ExecutionStatusBadge';

const nodeTypeIconMap: Record<string, React.ReactNode> = {
  BusinessEventTrigger: <LucideZap className="size-4" />,
  HttpWebhookTrigger: <LucideGlobe className="size-4" />,
  ScheduleTrigger: <LucideCalendar className="size-4" />,
  EmailAction: <LucideMail className="size-4" />,
  HttpRequestAction: <LucideGlobe className="size-4" />,
  Condition: <LucideGitBranch className="size-4" />,
  Delay: <LucideTimer className="size-4" />,
  Approval: <LucideShieldCheck className="size-4" />,
};

const nodeStatusIcon: Record<NodeExecutionStatus, React.ReactNode> = {
  Success: <LucideCircleCheck className="size-4 text-success" />,
  Failed: <LucideCircleX className="size-4 text-failure" />,
  Skipped: <LucideMinusCircle className="size-4 text-neutral/40" />,
};

const nodeStatusColor: Record<NodeExecutionStatus, string> = {
  Success: 'success',
  Failed: 'failure',
  Skipped: 'neutral',
};

const formatDuration = (ms: number | null): string => {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
};

const JsonExpandable: React.FC<{
  label: string;
  data: Record<string, unknown> | null;
}> = ({ label, data }) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!data || Object.keys(data).length === 0) return null;

  return (
    <Col className="@JsonExpandable">
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-neutral/70 hover:text-neutral"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? (
          <LucideChevronDown className="size-3.5" />
        ) : (
          <LucideChevronRight className="size-3.5" />
        )}
        {label}
      </button>
      {expanded && (
        <pre className="mt-1 max-h-60 overflow-auto rounded bg-neutral/5 p-3 text-xs">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </Col>
  );
};

const NodeExecutionRow: React.FC<{
  node: NodeExecutionRecord;
  t: ReturnType<typeof useTranslations>;
}> = ({ node, t }) => {
  const [expanded, setExpanded] = React.useState(false);
  const icon = nodeTypeIconMap[node.nodeType] ?? (
    <LucideZap className="size-4" />
  );

  return (
    <div
      className={cn(
        '@NodeExecutionRow',
        cn(
          'border-b border-neutral/10 last:border-b-0',
          node.status === 'Failed' && 'bg-failure/5',
        ),
      )}
    >
      <button
        type="button"
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-neutral/5"
        onClick={() => setExpanded((prev) => !prev)}
      >
        {expanded ? (
          <LucideChevronDown className="size-3.5 flex-shrink-0 text-neutral/40" />
        ) : (
          <LucideChevronRight className="size-3.5 flex-shrink-0 text-neutral/40" />
        )}
        <span className="flex-shrink-0 text-neutral/60">{icon}</span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium">
          {node.nodeId}
        </span>
        <Badge
          color={
            nodeStatusColor[node.status] as 'success' | 'failure' | 'neutral'
          }
          size="xs"
        >
          {t.executionDetail.nodeStatus[node.status]}
        </Badge>
        <span className="flex-shrink-0 text-xs text-neutral/50">
          {formatDuration(node.durationMs)}
        </span>
        {nodeStatusIcon[node.status]}
      </button>
      {expanded && (
        <Col className="gap-2 px-4 pb-3 pl-12">
          {node.status === 'Failed' && node.errorMessage && (
            <div className="rounded bg-failure/10 p-2 text-sm text-failure">
              {node.errorMessage}
            </div>
          )}
          <Row className="gap-4">
            <span className="text-xs text-neutral/50">
              {t.executionDetail.nodeHeaders.type}: {node.nodeType}
            </span>
            <span className="text-xs text-neutral/50">
              <LocalizedDate date={node.startedAt} />
            </span>
          </Row>
          <JsonExpandable
            label={t.executionDetail.nodeHeaders.input}
            data={node.input}
          />
          <JsonExpandable
            label={t.executionDetail.nodeHeaders.output}
            data={node.output}
          />
        </Col>
      )}
    </div>
  );
};

export const ExecutionRunDetailView: React.FC = () => {
  const [, navigate] = useLocation();
  const t = useTranslations();

  const automationId = useAtomValue($executionRunAutomationId);
  const { data, isLoading, error } = useAtomValue($remoteExecutionRunDetail);

  const handleBack = React.useCallback(() => {
    if (automationId) {
      navigate(automationsRoutes.detail.replace(':id', automationId));
    } else {
      navigate(automationsRoutes.list);
    }
  }, [automationId, navigate]);

  return (
    <Col className="@ExecutionRunDetailView h-full gap-0">
      <div className="border-b border-neutral/10 px-6 py-4">
        <Button variant="ghost" size="small" onPress={handleBack}>
          <LucideArrowLeft className="mr-1 size-4" />
          {t.executionDetail.back}
        </Button>
      </div>

      <AsyncBoundary error={error} loading={isLoading}>
        {data && (
          <Col className="flex-1 gap-6 overflow-auto p-6">
            <Row className="flex-wrap items-center gap-4">
              <h1 className="text-xl font-semibold">
                {t.executionDetail.title}
              </h1>
              <ExecutionStatusBadge status={data.status} />
              {data.isTest && (
                <Badge color="neutral" size="small">
                  {t.executionDetail.test}
                </Badge>
              )}
            </Row>

            <Row className="flex-wrap gap-6 text-sm text-neutral/60">
              <Col>
                <span className="text-xs font-medium text-neutral/40 uppercase">
                  {t.executionDetail.triggeredAt}
                </span>
                <LocalizedDate date={data.triggeredAt} />
              </Col>
              {data.completedAt && (
                <Col>
                  <span className="text-xs font-medium text-neutral/40 uppercase">
                    {t.executionDetail.completedAt}
                  </span>
                  <LocalizedDate date={data.completedAt} />
                </Col>
              )}
              <Col>
                <span className="text-xs font-medium text-neutral/40 uppercase">
                  {t.executionDetail.duration}
                </span>
                <span>{formatDuration(data.durationMs)}</span>
              </Col>
              {data.nodesExecuted !== null && (
                <Col>
                  <span className="text-xs font-medium text-neutral/40 uppercase">
                    {t.executionDetail.nodesExecuted}
                  </span>
                  <span>{data.nodesExecuted}</span>
                </Col>
              )}
            </Row>

            {data.status === 'Failed' && data.errorMessage && (
              <div className="rounded-lg bg-failure/10 p-4 text-sm text-failure">
                <span className="font-medium">
                  {t.executionDetail.nodeHeaders.error}:
                </span>{' '}
                {data.errorMessage}
              </div>
            )}

            <JsonExpandable
              label={t.executionDetail.triggerPayload}
              data={data.triggerPayload}
            />

            <Col className="gap-2">
              <h2 className="text-lg font-semibold">
                {t.executionDetail.nodeTrace}
              </h2>
              <Card className="overflow-hidden p-0">
                <div className="border-b border-neutral/10 bg-neutral/5 px-4 py-2">
                  <Row className="gap-3 text-xs font-medium text-neutral/50 uppercase">
                    <span className="w-5" />
                    <span className="w-5" />
                    <span className="flex-1">
                      {t.executionDetail.nodeHeaders.node}
                    </span>
                    <span className="w-20 text-center">
                      {t.executionDetail.nodeHeaders.status}
                    </span>
                    <span className="w-16 text-right">
                      {t.executionDetail.nodeHeaders.duration}
                    </span>
                    <span className="w-5" />
                  </Row>
                </div>
                {data.nodeExecutions.length === 0 ? (
                  <div className="py-8 text-center text-sm text-neutral/40">
                    {t.executionDetail.noNodes}
                  </div>
                ) : (
                  data.nodeExecutions.map((node) => (
                    <NodeExecutionRow key={node.nodeId} node={node} t={t} />
                  ))
                )}
              </Card>
            </Col>
          </Col>
        )}
      </AsyncBoundary>
    </Col>
  );
};
